"use client";
import { IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { toast } from "sonner";
import type { BlockUpdateFunction } from "./utils/blocks-map";
import AdminPopover from "./utils/AdminPopover";
import RenderIconOrAsset from "./utils/RenderIconOrAsset";
import BlockClickHandler from "./utils/click";
import { cn, shadow } from "@/lib/utils";
import {
    BORDER_RADIUS_OPTIONS,
    FONT_SIZE_OPTIONS,
    SHADOW_OPTIONS,
} from "./utils/block-style-classes";

interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
}
export default function BlockEmail({
    edit,
    account,
    block,
    update,
    style,
    theme,
}: Props) {
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const dataAttributes = Object.fromEntries(
        Object.entries(blockUpdate.metadata || {})
            .filter(([key]) => key.startsWith("html-data-"))
            .map(([key, value]) => [
                `data-metadata-${key.replace("html-data-", "")}`,
                value,
            ])
    );
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={"Email | " + block.url}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div className={`w-full`}>
                <button
                    style={style}
                    id="email-block"
                    className={cn(
                        "rounded-user bg-user-primary-color text-user-primary-text-color",
                        SHADOW_OPTIONS[theme.profile.design.shadow]?.class,
                        blockUpdate.style?.custom &&
                            BORDER_RADIUS_OPTIONS[
                                blockUpdate.style.corner_radius
                            ]?.class,
                        blockUpdate.style?.custom &&
                            SHADOW_OPTIONS[blockUpdate.style.shadow]?.class,
                        blockUpdate.style?.custom &&
                            FONT_SIZE_OPTIONS[blockUpdate.style.font_size]
                                ?.class,
                        "w-full px-1 py-3 text-xl transition-all duration-150 hover:scale-105",
                        blockUpdate.name.replaceAll(" ", "-"),
                        "block-" + block.id
                    )}
                    onClick={async (e) => {
                        try {
                            toast.success(
                                `${account.displayname}'s email copied to clipboard`
                            );
                            await navigator.clipboard.writeText(
                                blockUpdate.url
                            );
                            BlockClickHandler(account, e, block);
                        } catch (err) {
                            console.error("Failed to copy text: ", err);
                            toast.error("Failed to copy to clipboard");
                        }
                    }}
                    {...dataAttributes}
                >
                    <RenderIconOrAsset
                        account_id={account.id}
                        iconName={blockUpdate.icon}
                        size={
                            (blockUpdate.style?.custom &&
                                FONT_SIZE_OPTIONS[blockUpdate.style.font_size]
                                    ?.icon) ||
                            FONT_SIZE_OPTIONS[theme.profile.design.font_size]
                                ?.icon ||
                            26
                        }
                    />
                    <span
                        id="block-text"
                        className={`block w-full px-6 text-center font-bold`}
                    >
                        {blockUpdate.name}
                    </span>
                </button>
            </div>
        </AdminPopover>
    );
}
